/**
 * `OnchainActivityPanel` — the Overview blockchain-activity widget.
 *
 * Shows recent cross-chain activity as either a virtualised **Table**
 * (`@authe/tx-table`) or an interactive multichain **Graph** (`@authe/tx-graph`),
 * with a Live / Auto / Off update control (`@authe/ui-core`). View + live-mode
 * choices persist via the shared `DisplayPrefsStore`. All field names,
 * formatting, view-mode and live-mode vocabulary come from `@authe/fe-commons` +
 * the lower packages, so this surface stays consistent with the rest of the app.
 *
 * This is the *onchain* surface — recent blockchain transactions + the
 * interaction graph. It is intentionally separate from the in-app event inbox at
 * `/activity` (`@authe/notification-center`), which tracks app/collaboration
 * events. Two distinct feeds, clearly named.
 */
import * as React from "react";
import { Link } from "@tanstack/react-router";
import { RefreshCw, Share2, Table2, Wallet as WalletIcon } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  LiveModeControl,
  Skeleton,
  ViewModeToggle,
  cn,
  useDisplayPref,
} from "@authe/ui-core";
import { TransactionTable, type TxRow } from "@authe/tx-table";
import type { GraphShareState, TxGraphNode } from "@authe/tx-graph";
import { AccessWarningBanner, type AccessWarning } from "@authe/saved-views";
import {
  ACTIVITY_VIEWS,
  type ActivityView,
} from "@authe/fe-commons/display";
import { ROUTES } from "@/config";
import { displayPrefs } from "@/app/prefs";
import { useLiveModePref } from "@/app/liveMode";
import { useGraphViewModePref } from "@/features/graphs/graphViewModePref";
import { useOnchainActivity } from "./useOnchainActivity";
import { OnchainLiveFeed } from "./OnchainLiveFeed";
import { ACTIVITY_VIEW_HEIGHT } from "./constants";
import { useSubjectOpener, useFocusDrilldown } from "@/features/inspector";

/**
 * The graph half, fetched when the reader opens the Graph view.
 *
 * Both halves come out of ONE module, so the chunk arrives once and serves the
 * bar in the header and the canvas in the content. See
 * `./OnchainActivityGraph` for why the boundary has to enclose all of it — the
 * tx-graph bundle cannot be shaken apart, so one static barrel import anywhere
 * eager puts React Flow, `@xyflow/system` and dagre back in the landing chunk.
 */
const LazyOnchainGraphViewsBar = React.lazy(async () => {
  const mod = await import("./OnchainActivityGraph");
  return { default: mod.OnchainGraphViewsBar };
});
const LazyOnchainGraph = React.lazy(async () => {
  const mod = await import("./OnchainActivityGraph");
  return { default: mod.OnchainGraph };
});

const VIEW_LABELS: Record<ActivityView, React.ReactNode> = { table: "Table", graph: "Graph" };
const VIEW_ICONS: Record<ActivityView, React.ReactNode> = {
  table: <Table2 className="h-3.5 w-3.5" />,
  graph: <Share2 className="h-3.5 w-3.5" />,
};
const VIEW_OPTIONS = ACTIVITY_VIEWS.map((value) => ({
  value,
  label: VIEW_LABELS[value],
  icon: VIEW_ICONS[value],
}));

/** Pull a bare EVM address out of a chain-scoped node id (`chain#0x…`). */
function addressFromNodeId(id: string): string | undefined {
  const last = id.includes("#") ? id.slice(id.lastIndexOf("#") + 1) : id;
  return /^0x[0-9a-fA-F]{40}$/.test(last) ? last : undefined;
}


export function OnchainActivityPanel(): React.ReactElement {
  const opener = useSubjectOpener();
  // A clicked hash must answer the way a clicked ROW does. Without this the
  // default columns fall back to a block-explorer link, so this panel's own
  // rows opened the product and the hashes inside them opened Etherscan.
  const drilldown = useFocusDrilldown(opener);

  const [view, setView] = useDisplayPref<ActivityView>(
    () => displayPrefs.getActivityView(),
    (v) => displayPrefs.setActivityView(v),
  );
  // Honour the stored choice, but never run Live where this session has no
  // server streaming (the plan does not include it, or no endpoint is wired).
  const {
    mode: liveMode,
    setMode: setLiveMode,
    effectiveMode,
    liveAvailable,
    liveHidden,
  } = useLiveModePref();

  // Overlays only while the graph is the view on screen — it is the only one
  // that draws them, and the only one that mints a share link from them.
  const feed = useOnchainActivity({
    liveMode: effectiveMode,
    resolveOverlays: view === "graph",
  });
  const liveActive = liveAvailable && effectiveMode === "live";

  // Graph layout mode is lifted here so the saved-views bar can snapshot it and
  // the canvas can render either the live feed or an applied saved snapshot.
  const [graphMode, setGraphMode] = useGraphViewModePref();

  // A saved graph view, when applied, replaces the live feed on the canvas;
  // resetting it (`null`) returns to the live graph.
  const [appliedGraph, setAppliedGraph] = React.useState<GraphShareState | null>(null);
  /** The rendered canvas box, so the views bar's export menu can offer the PNG. */
  const canvasRef = React.useRef<HTMLDivElement | null>(null);
  const [warnings, setWarnings] = React.useState<AccessWarning[]>([]);

  const onRowClick = React.useCallback(
    (row: TxRow) => opener.openTransaction(row),
    [opener],
  );

  const onNodeClick = React.useCallback(
    (node: TxGraphNode) => {
      const address = addressFromNodeId(node.id);
      if (address) opener.openAddress(address);
    },
    [opener],
  );

  return (
    <Card className="mt-6">
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <CardTitle>{feed.hasWallet ? "Your onchain activity" : "Live blockchain activity"}</CardTitle>
          <CardDescription>
            {feed.hasWallet
              ? "Recent cross-chain history for your connected wallets, discovered across their active chains."
              : "The latest cross-chain transactions. Connect a wallet to see your own activity."}
          </CardDescription>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {liveActive && (
            <OnchainLiveFeed
              enabled
              onRows={feed.setStreamedRows}
              count={feed.streamedCount}
              addresses={feed.addresses}
            />
          )}
          <LiveModeControl
            mode={liveMode}
            onChange={setLiveMode}
            liveAvailable={liveAvailable}
            liveHidden={liveHidden}
            size="sm"
          />
          <ViewModeToggle<ActivityView>
            aria-label="Activity view"
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
            size="sm"
          />
          {view === "graph" && (
            // `null` while the chunk arrives: a toolbar that pops in is better
            // than a placeholder shaped like one, and the canvas below already
            // shows that the graph is loading.
            <React.Suspense fallback={null}>
              <LazyOnchainGraphViewsBar
                graph={feed.graph}
                resolveOverlay={feed.resolveOverlay}
                graphMode={graphMode}
                appliedGraph={appliedGraph}
                onApplyGraph={setAppliedGraph}
                onWarnings={setWarnings}
                // So the bar's export menu can offer the picture too.
                canvasRef={canvasRef}
              />
            </React.Suspense>
          )}
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={() => feed.refetch()}
            disabled={feed.isFetching}
            aria-label="Refresh activity"
            title="Refresh"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", feed.isFetching && "animate-spin")} />
          </Button>
          {!feed.hasWallet && (
            <Button asChild size="sm" variant="outline">
              <Link to={ROUTES.wallet}>
                <WalletIcon className="h-4 w-4" />
                Connect wallet
              </Link>
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {view === "table" ? (
          <>
            <TransactionTable
              rows={feed.rows}
              onDrilldown={drilldown}
              isLoading={feed.isLoading}
              isFetching={feed.isFetching}
              error={feed.error}
              onRetry={feed.refetch}
              onRowClick={onRowClick}
              enableToolbar={false}
              density="compact"
              height={ACTIVITY_VIEW_HEIGHT}
              emptyTitle="No recent activity"
              emptyDescription={
                feed.hasWallet
                  ? "We couldn't find recent transactions for the connected wallets."
                  : "No transactions returned from the preview chains right now."
              }
            />
            {/* Only the wallet feed pages; the no-wallet preview is each chain's
                latest block, which has no "next". */}
            {feed.hasMore && (
              <div className="mt-3 flex justify-center">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={feed.loadMore}
                  disabled={feed.isLoadingMore}
                >
                  {feed.isLoadingMore ? "Loading…" : "Load more"}
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <AccessWarningBanner warnings={warnings} className="mb-3" />
            <React.Suspense
              fallback={<Skeleton className={cn("w-full rounded-md", ACTIVITY_VIEW_HEIGHT)} />}
            >
              <LazyOnchainGraph
                graph={feed.graph}
                resolveOverlay={feed.resolveOverlay}
                onNodeClick={onNodeClick}
                hasWallet={feed.hasWallet}
                isLoading={feed.isLoading}
                error={feed.error}
                onRetry={feed.refetch}
                appliedGraph={appliedGraph}
                graphMode={graphMode}
                onGraphModeChange={setGraphMode}
                onResetToLive={() => setAppliedGraph(null)}
                canvasRef={canvasRef}
              />
            </React.Suspense>
          </>
        )}
      </CardContent>
    </Card>
  );
}
